/**
 * One encrypted attachment as a single byte string: the server's download
 * format, also used for the copies kept on the device
 * (docs/plans/2026-09-29-attachments-move-with-their-map.md).
 *
 *   [u32 big-endian header length][JSON header][encrypted bytes]
 *
 * Header: { wrapped_bek, filename_encrypted, mime_type, size_original }
 * (base64 for the two binary fields), plus `created_at` in device copies.
 * Nothing here is secret: the file and its name are encrypted, the key is
 * wrapped by the map's key. (Files waiting to be sent also keep their own
 * key and name until sent: see waiting-attachments.js.)
 */

const MAX_HEADER = 64 * 1024;

/**
 * @param {{wrapped_bek: string, filename_encrypted?: string|null, mime_type: string,
 *   size_original: number, created_at?: string}} header
 * @param {Uint8Array} encrypted
 * @returns {Uint8Array}
 */
export function buildEnvelope(header, encrypted) {
  const headerBytes = new TextEncoder().encode(JSON.stringify(header));
  if (headerBytes.length > MAX_HEADER) throw new Error('Attachment header too large');
  const out = new Uint8Array(4 + headerBytes.length + encrypted.length);
  new DataView(out.buffer).setUint32(0, headerBytes.length);
  out.set(headerBytes, 4);
  out.set(encrypted, 4 + headerBytes.length);
  return out;
}

export function uint8ToBase64(bytes) {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

export function base64ToUint8(b64) {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/** The header only (cheap: used to list stored files). */
export function readEnvelopeHeader(bytes) {
  return parseEnvelope(bytes).header;
}

/**
 * @param {ArrayBuffer|Uint8Array} bytes
 * @returns {{header: object, encrypted: Uint8Array}}
 */
export function parseEnvelope(bytes) {
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  if (view.length < 4) throw new Error('Attachment data is truncated');
  const headerLen = new DataView(view.buffer, view.byteOffset, view.byteLength).getUint32(0);
  if (headerLen > MAX_HEADER || 4 + headerLen > view.length) throw new Error('Attachment data is truncated');
  const header = JSON.parse(new TextDecoder().decode(view.subarray(4, 4 + headerLen)));
  if (typeof header?.wrapped_bek !== 'string' || typeof header?.mime_type !== 'string') {
    throw new Error('Attachment header is incomplete');
  }
  return { header, encrypted: view.subarray(4 + headerLen) };
}
